import React, { FC } from 'react';
import DataTable from 'components/DataTable/DataTable';
import { User } from 'dataTypes/user';
import { Box, MenuItem, Select } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EmptyTable from 'components/DataTable/components/EmptyTable/EmptyTable';
import NotFound from 'assets/images/not_found.svg';
import styles from './UserTable.module.css';
import { useAppSelector } from 'app/hooks';
import { selectCurrentUser } from 'features/auth/authSlice';
import { formatTextUpperCase } from 'utils/formatName';
import { usePatchUserMutation } from 'features/user/userApi';
import { UserRoles } from 'features/auth/authTypes';

interface UserTableProps {
  data: User[];
}

const UserTable: FC<UserTableProps> = ({ data }) => {
  const selector = useAppSelector;
  const user = selector(state => selectCurrentUser(state));
  const [patchUser] = usePatchUserMutation();
  const isAdmin = user ? user.role === 'admin' : false;

  const onChangeUserRole = (value: string, id: number) => {
    patchUser({ id, role: value }).unwrap();
  };

  if (!data.length) {
    return (
      <EmptyTable
        imgSrc={NotFound}
        title='No results were found'
        subtitle='Try entering another information'
        smallImage
      />
    );
  }

  const rowsData = data.map(({ name, email, role, salesforceRole, id }) => {
    const appRole = role === 'admin' ? role : 'editor';
    const roleLabel = formatTextUpperCase(appRole || '');
    return {
      name,
      email,
      salesforceRole: salesforceRole ?? 'N/A',
      role: isAdmin ? (
        <Select
          className={styles.userRoleSelect}
          disabled={id === user?.id}
          id='user-role-select'
          value={role || 'admin'}
          onChange={event => onChangeUserRole(event.target.value, id)}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            className: styles.userRoleMenu,
          }}
        >
          <MenuItem value={UserRoles.ADMIN}>Admin</MenuItem>
          <MenuItem value={UserRoles.EDITOR}>Editor</MenuItem>
          <MenuItem value={UserRoles.PDF_ONLY}>PDF Only</MenuItem>
        </Select>
      ) : (
        roleLabel
      ),
    };
  });

  return (
    <Box width='100%'>
      <DataTable
        columnData={[
          {
            id: 'name',
            name: 'Name',
            align: 'left',
          },
          { id: 'email', name: 'Email', align: 'left' },
          { id: 'salesforce-role', name: 'Salesforce Role', align: 'left' },
          {
            id: 'user-role',
            name: 'User Role',
            align: 'left',
          },
        ]}
        rows={rowsData}
      />
    </Box>
  );
};

export default UserTable;
